/* eslint-disable max-lines */
import { useTenantsByName } from '@hakimo-ui/hakimo/data-access';
import {
  ApplianceFacets,
  AppliancesFilters,
  ApplianceType,
  NeedsAttentionReason,
} from '@hakimo-ui/hakimo/types';
import { SlideOver } from '@hakimo-ui/hakimo/ui-elements';
import {
  FilterFooter,
  FilterSection,
  MultiSelectFilter,
} from '@hakimo-ui/hakimo/ui-filters';
import { Checkbox, Selectable } from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import {
  APPLIANCE_TYPE_OPTIONS,
  defaultFilters,
  humanizeType,
  NEEDS_ATTENTION_OPTIONS,
} from './util';

interface Props {
  open: boolean;
  filters: AppliancesFilters;
  facets?: ApplianceFacets;
  tenantNames?: Record<string, string>;
  onClose: () => void;
  onApplyFilters: (filters: AppliancesFilters) => void;
}

interface ContentProps {
  filters: AppliancesFilters;
  facets?: ApplianceFacets;
  tenantNames?: Record<string, string>;
  onApplyFilters: (filters: AppliancesFilters) => void;
}

function toggle<T extends string>(values: T[], value: T): T[] {
  return values.includes(value)
    ? values.filter((v) => v !== value)
    : [...values, value];
}

function CheckRow(props: {
  label: string;
  count?: number;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <Checkbox
        label={props.label}
        checked={props.checked}
        onChange={props.onChange}
      />
      {props.count !== undefined && (
        <span className="dark:text-ondark-text-3 text-onlight-text-3 text-xs">
          {props.count}
        </span>
      )}
    </div>
  );
}

function AppliancesFilterPanelContent(props: ContentProps) {
  const { filters, facets, tenantNames, onApplyFilters } = props;
  const [local, setLocal] = useState<AppliancesFilters>(filters);
  const [selectedTenants, setSelectedTenants] = useState<Selectable[]>(() =>
    filters.tenantId.map((id) => ({ id, name: tenantNames?.[id] ?? id }))
  );
  const searchTenants = useTenantsByName();

  const toggleApplianceType = (value: ApplianceType) =>
    setLocal({ ...local, applianceType: toggle(local.applianceType, value) });

  const toggleNeedsAttention = (value: NeedsAttentionReason) =>
    setLocal({
      ...local,
      needsAttention: toggle(local.needsAttention, value),
    });

  const onChangeTenants = (selected: Selectable[]) => {
    setSelectedTenants(selected);
    setLocal({ ...local, tenantId: selected.map((o) => o.id) });
  };

  const onReset = () => {
    setLocal(defaultFilters);
    setSelectedTenants([]);
  };

  return (
    <div className="flex h-full flex-col space-y-6">
      <div className="dark:divide-dark-border-bg dark:border-dark-border-bg divide-y border-b border-t">
        <FilterSection
          title="Appliance type"
          hasFilterApplied={local.applianceType.length > 0}
          content={
            <div>
              {APPLIANCE_TYPE_OPTIONS.map((type) => (
                <CheckRow
                  key={type}
                  label={humanizeType(type)}
                  count={facets?.applianceType[type]}
                  checked={local.applianceType.includes(type)}
                  onChange={() => toggleApplianceType(type)}
                />
              ))}
            </div>
          }
        />
        <FilterSection
          title="Tenant"
          hasFilterApplied={local.tenantId.length > 0}
          content={
            <MultiSelectFilter
              label=""
              value={{ values: selectedTenants, negative: false }}
              onChange={(v) => onChangeTenants(v.values)}
              getOptions={searchTenants}
            />
          }
        />
        <FilterSection
          title="Needs attention"
          hasFilterApplied={local.needsAttention.length > 0}
          content={
            <div>
              {NEEDS_ATTENTION_OPTIONS.map((opt) => (
                <CheckRow
                  key={opt.id}
                  label={opt.label}
                  count={facets?.needsAttention[opt.id]}
                  checked={local.needsAttention.includes(opt.id)}
                  onChange={() => toggleNeedsAttention(opt.id)}
                />
              ))}
            </div>
          }
        />
      </div>
      <div className="grow" />
      <FilterFooter
        onApply={() => onApplyFilters(local)}
        onReset={onReset}
        trackingEventNameApply="apply_appliances_filters"
        trackingEventNameReset="reset_appliances_filters"
      />
    </div>
  );
}

export function AppliancesFilterPanel(props: Props) {
  const { open, filters, facets, tenantNames, onClose, onApplyFilters } = props;

  return (
    <SlideOver title="Appliance Filters" open={open} onClose={onClose}>
      <AppliancesFilterPanelContent
        filters={filters}
        facets={facets}
        tenantNames={tenantNames}
        onApplyFilters={onApplyFilters}
      />
    </SlideOver>
  );
}

export default AppliancesFilterPanel;
